import { useState, useEffect } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Chip,
  Tabs,
  Tab,
  Alert,
  Snackbar,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Code as CodeIcon
} from '@mui/icons-material'
import { apiClient } from '../../../../shared/services/ApiClient'

interface EmailTemplate {
  emailTemplateId: string
  name: string
  type: string
  subject: string
  htmlBody: string
  textBody: string
  isActive: boolean
  createdAt: string
  updatedAt: string
}

interface TemplateType {
  type: string
  name: string
  description: string
}

function EmailTemplates() {
  const [templates, setTemplates] = useState<EmailTemplate[]>([])
  const [templateTypes, setTemplateTypes] = useState<TemplateType[]>([])
  const [loading, setLoading] = useState(false)
  const [openDialog, setOpenDialog] = useState(false)
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
  const [openPreviewDialog, setOpenPreviewDialog] = useState(false)
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null)
  const [editMode, setEditMode] = useState(false)
  const [tabValue, setTabValue] = useState(0)
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' })

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    type: '',
    subject: '',
    htmlBody: '',
    textBody: '',
    isActive: true
  })

  // Preview state
  const [previewHtml, setPreviewHtml] = useState('')
  const [previewSubject, setPreviewSubject] = useState('')

  useEffect(() => {
    loadTemplates()
    loadTemplateTypes()
  }, [])

  const loadTemplates = async () => {
    try {
      setLoading(true)
      const data = await apiClient.get<EmailTemplate[]>('/EmailTemplate')
      setTemplates(data)
    } catch (error) {
      showSnackbar('Failed to load templates', 'error')
    } finally {
      setLoading(false)
    }
  }

  const loadTemplateTypes = async () => {
    try {
      const data = await apiClient.get<TemplateType[]>('/EmailTemplate/types')
      setTemplateTypes(data)
    } catch (error) {
      console.error('Failed to load template types:', error)
    }
  }

  const handleOpenDialog = (template?: EmailTemplate) => {
    if (template) {
      setEditMode(true)
      setSelectedTemplate(template)
      setFormData({
        name: template.name,
        type: template.type,
        subject: template.subject,
        htmlBody: template.htmlBody,
        textBody: template.textBody,
        isActive: template.isActive
      })
    } else {
      setEditMode(false)
      setSelectedTemplate(null)
      setFormData({
        name: '',
        type: '',
        subject: '',
        htmlBody: '',
        textBody: '',
        isActive: true
      })
    }
    setOpenDialog(true)
  }

  const handleCloseDialog = () => {
    setOpenDialog(false)
    setSelectedTemplate(null)
    setEditMode(false)
  }

  const handleSave = async () => {
    try {
      setLoading(true)

      if (editMode && selectedTemplate) {
        // Update existing template
        await apiClient.put<EmailTemplate>(`/EmailTemplate/${selectedTemplate.emailTemplateId}`, formData)
        showSnackbar('Template updated successfully', 'success')
        loadTemplates()
        handleCloseDialog()
      } else {
        // Create new template
        await apiClient.post<EmailTemplate>('/EmailTemplate', formData)
        showSnackbar('Template created successfully', 'success')
        loadTemplates()
        handleCloseDialog()
      }
    } catch (error: any) {
      const message = error.message || 'Failed to save template'
      showSnackbar(message, 'error')
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async () => {
    if (!selectedTemplate) return

    try {
      setLoading(true)
      await apiClient.delete<void>(`/EmailTemplate/${selectedTemplate.emailTemplateId}`)
      showSnackbar('Template deleted successfully', 'success')
      loadTemplates()
      setOpenDeleteDialog(false)
      setSelectedTemplate(null)
    } catch (error) {
      showSnackbar('Failed to delete template', 'error')
    } finally {
      setLoading(false)
    }
  }

  const handlePreview = async (template: EmailTemplate) => {
    try {
      setSelectedTemplate(template)

      // Create sample data with common template variables
      const sampleData: { [key: string]: string } = {
        FirstName: 'John',
        LastName: 'Doe',
        Email: 'john.doe@example.com',
        TicketNumber: 'TKT-12345',
        TicketSubject: 'Sample Support Ticket',
        TicketStatus: 'Open',
        TicketPriority: 'High',
        CreatedAt: new Date().toLocaleDateString(),
        CompanyName: 'Your Helpdesk',
        SupportEmail: 'support@example.com',
        VerificationLink: 'https://example.com/verify',
        TicketUrl: 'https://example.com/tickets/12345',
        CustomerName: 'John Doe',
        AgentName: 'Jane Smith'
      }

      const data = await apiClient.post<{ htmlBody: string; textBody: string; subject: string }>(`/EmailTemplate/${template.emailTemplateId}/preview`, sampleData)
      setPreviewHtml(data.htmlBody)
      setPreviewSubject(data.subject)
      setOpenPreviewDialog(true)
    } catch (error) {
      showSnackbar('Failed to preview template', 'error')
    }
  }

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity })
  }

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue)
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Email Templates</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Create Template
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        Email templates are used for automated emails sent by the system. Use variables like
        {' '}<code>&#123;&#123;FirstName&#125;&#125;</code> to personalize emails.
      </Alert>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Subject</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Last Updated</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && templates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">Loading...</TableCell>
              </TableRow>
            ) : templates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No templates found. Create your first template to get started.
                </TableCell>
              </TableRow>
            ) : (
              templates.map((template) => (
                <TableRow key={template.emailTemplateId}>
                  <TableCell>{template.name}</TableCell>
                  <TableCell>
                    <Chip label={template.type} size="small" />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {template.subject}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={template.isActive ? 'Active' : 'Inactive'}
                      color={template.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {new Date(template.updatedAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handlePreview(template)}
                      title="Preview"
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(template)}
                      title="Edit"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSelectedTemplate(template)
                        setOpenDeleteDialog(true)
                      }}
                      title="Delete"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="lg" fullWidth>
        <DialogTitle>
          {editMode ? 'Edit Email Template' : 'Create Email Template'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Template Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              sx={{ mb: 2 }}
              required
            />

            <FormControl fullWidth sx={{ mb: 2 }} disabled={editMode}>
              <InputLabel>Template Type</InputLabel>
              <Select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                label="Template Type"
                required
              >
                {templateTypes.map((type) => (
                  <MenuItem key={type.type} value={type.type}>
                    {type.name} - {type.description}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Subject Line"
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              sx={{ mb: 2 }}
              required
              helperText="Use variables like {{FirstName}}, {{TicketNumber}}, etc."
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label="Active"
              sx={{ mb: 2 }}
            />

            <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2 }}>
              <Tab label="HTML Body" />
              <Tab label="Plain Text Body" />
              <Tab label="Variables" />
            </Tabs>

            {tabValue === 0 && (
              <TextField
                fullWidth
                label="HTML Body"
                value={formData.htmlBody}
                onChange={(e) => setFormData({ ...formData, htmlBody: e.target.value })}
                multiline
                rows={15}
                required
                sx={{ fontFamily: 'monospace' }}
              />
            )}

            {tabValue === 1 && (
              <TextField
                fullWidth
                label="Plain Text Body"
                value={formData.textBody}
                onChange={(e) => setFormData({ ...formData, textBody: e.target.value })}
                multiline
                rows={15}
                sx={{ fontFamily: 'monospace' }}
              />
            )}

            {tabValue === 2 && (
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Available Variables</Typography>
                  <Typography variant="body2" paragraph>
                    Use these variables in your template to personalize emails:
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                    {[
                      '{{FirstName}}',
                      '{{LastName}}',
                      '{{Email}}',
                      '{{TicketNumber}}',
                      '{{TicketSubject}}',
                      '{{TicketStatus}}',
                      '{{TicketPriority}}',
                      '{{TicketDescription}}',
                      '{{CustomerName}}',
                      '{{AgentName}}',
                      '{{CompanyName}}',
                      '{{SupportEmail}}',
                      '{{TicketUrl}}',
                      '{{VerificationLink}}',
                      '{{CreatedAt}}',
                      '{{UpdatedAt}}'
                    ].map((variable) => (
                      <Chip
                        key={variable}
                        label={variable}
                        size="small"
                        icon={<CodeIcon />}
                        onClick={() => {
                          navigator.clipboard.writeText(variable)
                          showSnackbar(`Copied ${variable} to clipboard`, 'success')
                        }}
                      />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={loading}>
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Delete Template</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the template "{selectedTemplate?.name}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={loading}>
            {loading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={openPreviewDialog} onClose={() => setOpenPreviewDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Template Preview: {selectedTemplate?.name}
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            This is a preview with sample data. Actual emails will use real data.
          </Alert>

          <Typography variant="subtitle2" gutterBottom>Subject:</Typography>
          <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.100' }}>
            <Typography>{previewSubject}</Typography>
          </Paper>

          <Typography variant="subtitle2" gutterBottom>HTML Preview:</Typography>
          <Paper sx={{ p: 2, maxHeight: 500, overflow: 'auto' }}>
            <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPreviewDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default EmailTemplates
